import { AccessLevel } from './access-level.model';
import { DocumentReferenceEntityType } from './document-reference.model';

/** `DocsTokenRequest.action` — 20 grants upload, 30 grants download. */
export enum DocumentTokenAction {
  UPLOAD = 20,
  DOWNLOAD = 30,
}

/**
 * Body of `POST /api/v2/documents/token`. Mints a short-lived JWT
 * authorising upload/download of the referenced document(s), and — for
 * `action: UPLOAD` — provisions the document + reference rows up front (the
 * "placeholder" row a WS `ins` would otherwise announce).
 */
export interface DocsTokenRequest {
  action: DocumentTokenAction;
  /** Owning entity id (e.g. the deal/Item id for `entityType: ITEM`). */
  entityId?: string;
  entityType?: DocumentReferenceEntityType;
  documentIds?: string[];
  categoryIds?: string[];
  userId?: string;
  contentType?: string;
  documentName?: string;
  accessLevel?: AccessLevel;
  categoryId?: string;
  workflowId?: string;
  prerequisiteId?: string;
}

/** Response of `POST /api/v2/documents/token`. */
export interface TokenApi {
  token: string;
  documentIds: string[];
  categoryIds?: string[];
  referenceId?: string;
  userId?: string;
}

/** One entry of `DocumentTokenPayload.documents` — the placeholder's document row. */
export interface DocumentTokenDocument {
  id: string;
  /** Placeholder name for a not-yet-filled row; the real filename once bytes land. */
  description: string;
}

/**
 * Decoded payload of the JWT minted by `POST /api/v2/documents/token`
 * (see `decodeDocumentToken` in `@qtek/shared/utils`). `documentIds[i]` /
 * `documentReferenceIds[i]` / `documents[i]` are index-parallel.
 *
 * `decodeDocumentToken` normalizes `documentIds`, `documentReferenceIds`
 * and `documents` to real arrays (never absent/malformed) on any non-null
 * result — so on a decoded token, treat these fields as required despite
 * the `?` on the two optional ones below. The server only mints `documents`
 * for one upload path (existing-document uploads to an item;
 * `HttpDocsToken.mintToken` in synply-api), so an empty array there is
 * normal, not a decode failure. Also note: the server's `entityId` claim
 * has no counterpart field here — `referenceId` below is never populated
 * by the decoder.
 */
export interface DocumentTokenPayload {
  sub: string;
  action: DocumentTokenAction;
  documentIds: string[];
  documentReferenceIds?: string[];
  referenceId?: string;
  referenceType?: DocumentReferenceEntityType;
  userId?: string;
  documents: DocumentTokenDocument[];
  exp: number;
}

/**
 * Body shared by `POST /api/v2/documents/{documentId}/send` and
 * `POST /api/v2/deals/{itemId}/notify-documents`.
 *
 * `destinations` is documented as email addresses — the legacy
 * `snd/new-deal-docs` endpoint took user ids instead (`dsts: userIds`), so
 * callers migrating that flow need to resolve emails client-side. Unverified
 * against a live request; see the data-room migration plan's open questions.
 */
export interface MessageSendRequest {
  destinations: string[];
  referenceIds?: string[];
  subject?: string;
  content?: string;
}

export interface ExtractionRequestResult {
  scanned: boolean;
  extractionRequested: boolean;
}
